import { timeModel } from "../../../models";
import { RelationshipProgressStatus, RelationshipStatus } from "../../../models/relationship";
import { SearchFilterType } from "../components/types";
import { FilterType, ProposalReportEntryStatus } from "./types";
import { endOfDay, startOfDay } from 'date-fns';
export let ProposalsReportSortColumnId = function (ProposalsReportSortColumnId) {
  ProposalsReportSortColumnId[ProposalsReportSortColumnId["CompanyName"] = FilterType.CompanyName] = "CompanyName";
  ProposalsReportSortColumnId[ProposalsReportSortColumnId["SentBy"] = FilterType.SentBy] = "SentBy";
  ProposalsReportSortColumnId[ProposalsReportSortColumnId["SentDate"] = FilterType.SentDate] = "SentDate";
  ProposalsReportSortColumnId[ProposalsReportSortColumnId["ApprovalDate"] = FilterType.ApprovalDate] = "ApprovalDate";
  ProposalsReportSortColumnId[ProposalsReportSortColumnId["Status"] = FilterType.Status] = "Status";
  return ProposalsReportSortColumnId;
}({});
export const proposalsReportHeaderItems = [{
  value: 'Company name',
  sortId: ProposalsReportSortColumnId.CompanyName
}, {
  value: 'Sent by',
  sortId: ProposalsReportSortColumnId.SentBy
}, {
  value: 'Sent date',
  sortId: ProposalsReportSortColumnId.SentDate
}, {
  value: 'Approval date',
  sortId: ProposalsReportSortColumnId.ApprovalDate
}, {
  value: 'Status',
  sortId: ProposalsReportSortColumnId.Status
}];
export const proposalsReportColumnDefs = [{
  width: 420,
  sortable: true
}, {
  width: 240,
  sortable: true
}, {
  width: 120,
  sortable: true
}, {
  width: 120,
  sortable: true
}, {
  width: 140,
  sortable: true
}];
export const statusLabelMap = {
  [ProposalReportEntryStatus.All]: 'All',
  [ProposalReportEntryStatus.NotReviewed]: 'Not reviewed',
  [ProposalReportEntryStatus.Reviewed]: 'Reviewed',
  [ProposalReportEntryStatus.Signed]: 'Signed',
  [ProposalReportEntryStatus.Expired]: 'Expired',
  [ProposalReportEntryStatus.Withdrawn]: 'Withdrawn',
  [ProposalReportEntryStatus.Terminated]: 'Terminated'
};
export const makeFilterConfig = _ref => {
  let {
    now,
    filtersOptions,
    data,
    isInitialSentDateFilter = false
  } = _ref;
  return {
    [FilterType.CompanyName]: {
      type: SearchFilterType.MultiSelectFilter,
      options: filtersOptions[FilterType.CompanyName] || [],
      removable: true,
      withSearchbox: true
    },
    [FilterType.SentBy]: {
      type: SearchFilterType.MultiSelectFilter,
      options: (filtersOptions[FilterType.SentBy] || []).map(id => {
        const entry = data === null || data === void 0 ? void 0 : data.find(e => e.sentById() === id);
        return {
          value: id,
          label: (entry === null || entry === void 0 ? void 0 : entry.sentByName()) || id
        };
      }),
      removable: true
    },
    [FilterType.SentDate]: {
      type: SearchFilterType.DateRangeWithPresets,
      removable: true,
      startOpen: !isInitialSentDateFilter,
      customDateLimits: {
        max: now,
        maxDateMessage: 'Sent date cannot be in the future'
      }
    },
    [FilterType.ApprovalDate]: {
      type: SearchFilterType.DateRangeWithPresets,
      removable: true,
      startOpen: true,
      customDateLimits: {
        max: now,
        maxDateMessage: 'Approval date cannot be in the future'
      }
    },
    [FilterType.Status]: {
      type: SearchFilterType.RadioFilter,
      options: Object.values(ProposalReportEntryStatus).map(value => ({
        value,
        label: statusLabelMap[value]
      })),
      removable: true,
      defaultOption: ProposalReportEntryStatus.All
    },
    [FilterType.AgreementName]: {
      type: SearchFilterType.MultiSelectFilter,
      options: filtersOptions[FilterType.AgreementName] || [],
      removable: true,
      withSearchbox: true
    }
  };
};
export const calculateStatus = summary => {
  if (summary.status === RelationshipStatus.Expired) return ProposalReportEntryStatus.Expired;
  if (summary.status === RelationshipStatus.Approved) return ProposalReportEntryStatus.Signed;
  if (summary.status === RelationshipStatus.Canceled) return ProposalReportEntryStatus.Withdrawn;
  if (summary.status === RelationshipStatus.Terminated) return ProposalReportEntryStatus.Terminated;
  if (summary.progressStatus === RelationshipProgressStatus.Sent && summary.status === RelationshipStatus.Pending) return ProposalReportEntryStatus.NotReviewed;
  if (summary.progressStatus === RelationshipProgressStatus.Reviewed && summary.status === RelationshipStatus.Pending) return ProposalReportEntryStatus.Reviewed;
  return ProposalReportEntryStatus.NotReviewed;
};
export const statusFilterMap = {
  [ProposalReportEntryStatus.Expired]: {
    status: RelationshipStatus.Expired
  },
  [ProposalReportEntryStatus.Signed]: {
    status: RelationshipStatus.Approved
  },
  [ProposalReportEntryStatus.Withdrawn]: {
    status: RelationshipStatus.Canceled
  },
  [ProposalReportEntryStatus.Terminated]: {
    status: RelationshipStatus.Terminated
  },
  [ProposalReportEntryStatus.NotReviewed]: {
    progressStatus: RelationshipProgressStatus.Sent,
    status: 'awaitingApproval'
  },
  [ProposalReportEntryStatus.Reviewed]: {
    progressStatus: RelationshipProgressStatus.Reviewed,
    status: 'awaitingApproval'
  },
  [ProposalReportEntryStatus.All]: {}
};
export const makeCsv = proposals => {
  const headersToExport = ['Company name', 'Sent by', 'Sent date', 'Approval date', 'Status'];
  return [headersToExport].concat(proposals.map(proposal => [proposal.companyName(), proposal.sentByName(), proposal.formattedSentDate(), proposal.formattedApprovalDate(), statusLabelMap[proposal.status()]]));
};
export const sortProposalsReportData = (proposals, sort) => {
  var _headerItems$colIndex;
  if (!sort) return proposals;
  const {
    colIndex,
    sort: sortDirection
  } = sort;
  const headerItems = proposalsReportHeaderItems;
  const colId = (_headerItems$colIndex = headerItems[colIndex]) === null || _headerItems$colIndex === void 0 ? void 0 : _headerItems$colIndex.sortId;
  if (!colId) return proposals;
  const sortFunctions = {
    [ProposalsReportSortColumnId.CompanyName]: (a, b) => a.companyName().localeCompare(b.companyName()),
    [ProposalsReportSortColumnId.SentBy]: (a, b) => a.sentByName().localeCompare(b.sentByName()),
    [ProposalsReportSortColumnId.SentDate]: (a, b) => a.sentDate().getTime() - b.sentDate().getTime(),
    [ProposalsReportSortColumnId.ApprovalDate]: (a, b) => {
      var _a$approvalDate, _b$approvalDate;
      return (((_a$approvalDate = a.approvalDate()) === null || _a$approvalDate === void 0 ? void 0 : _a$approvalDate.getTime()) || 0) - (((_b$approvalDate = b.approvalDate()) === null || _b$approvalDate === void 0 ? void 0 : _b$approvalDate.getTime()) || 0);
    },
    [ProposalsReportSortColumnId.Status]: (a, b) => a.status().localeCompare(b.status())
  };
  return proposals.slice().sort((a, b) => {
    const result = sortFunctions[colId](a, b);
    return sortDirection === 'asc' ? result : -result;
  });
};
export const applyFrontendFilters = (proposals, filters) => {
  var _filters$FilterType$S, _filters$FilterType$C, _filters$FilterType$A;
  let filteredProposals = proposals;
  if ((_filters$FilterType$S = filters[FilterType.SentDate]) !== null && _filters$FilterType$S !== void 0 && _filters$FilterType$S.dateRange) {
    const [startDate, endDate] = filters[FilterType.SentDate].dateRange;
    filteredProposals = filteredProposals.filter(proposal => {
      const sentDate = proposal.sentDate();
      if (startDate && sentDate < startOfDay(new Date(startDate))) return false;
      if (endDate && sentDate > endOfDay(new Date(endDate))) return false;
      return true;
    });
  }
  if ((_filters$FilterType$C = filters[FilterType.CompanyName]) !== null && _filters$FilterType$C !== void 0 && _filters$FilterType$C.length) {
    const companyNames = filters[FilterType.CompanyName];
    filteredProposals = filteredProposals.filter(proposal => companyNames.includes(proposal.companyName()));
  }
  if ((_filters$FilterType$A = filters[FilterType.AgreementName]) !== null && _filters$FilterType$A !== void 0 && _filters$FilterType$A.length) {
    const agreementNames = filters[FilterType.AgreementName];
    filteredProposals = filteredProposals.filter(proposal => agreementNames.includes(proposal.agreementName()));
  }
  return filteredProposals;
};
export const getProposalsReportFilterParams = (filters, isVendor) => {
  var _filters$FilterType$A2, _filters$FilterType$S2;
  const params = {
    party: isVendor ? 'vendor' : 'customer',
    status: 'approved,terminated,awaitingApproval,expired,canceled'
  };
  if (filters[FilterType.Status]) {
    if (filters[FilterType.Status] === ProposalReportEntryStatus.All) return params;
    const status = statusFilterMap[filters[FilterType.Status]];
    if (status.status) params.status = status.status;
    if (status.progressStatus) params.progressStatus = status.progressStatus;
  }
  if ((_filters$FilterType$A2 = filters[FilterType.ApprovalDate]) !== null && _filters$FilterType$A2 !== void 0 && _filters$FilterType$A2.dateRange) {
    const [startDate, endDate] = filters[FilterType.ApprovalDate].dateRange;
    if (startDate) params.approvedAtFrom = timeModel.toDatestampString(new Date(startDate));
    if (endDate) params.approvedAtTo = timeModel.toDatestampString(new Date(endDate));
  }
  if ((_filters$FilterType$S2 = filters[FilterType.SentBy]) !== null && _filters$FilterType$S2 !== void 0 && _filters$FilterType$S2.length) {
    const sellerIds = filters[FilterType.SentBy];
    params.sellerId = sellerIds.join(',');
  }
  return params;
};